import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Heart, Brain, Users, Gamepad2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const GamesPage = () => {
  const navigate = useNavigate();

  const games = [
    {
      id: 'us-again',
      name: 'US AGAIN',
      description: 'Fall in love again. Daily missions, connection meter, and surprise drops for couples!',
      icon: '💞',
      iconComponent: Sparkles,
      color: 'from-pink-500 via-purple-500 to-indigo-500',
      hoverColor: 'hover:border-pink-300',
      questions: 'For Couples',
      isNew: true
    },
    {
      id: 'get-to-know-you',
      name: 'Get to Know You',
      description: 'Fun questions to learn more about each other. Perfect for new matches!',
      icon: '💬',
      iconComponent: MessageCircle,
      color: 'from-blue-500 to-cyan-500',
      hoverColor: 'hover:border-blue-300',
      questions: '20+ questions'
    },
    {
      id: 'love-psychology-quiz',
      name: 'Love & Psychology Quiz',
      description: 'Discover your love language and relationship style through insightful questions.',
      icon: '💕',
      iconComponent: Heart,
      color: 'from-pink-500 to-rose-500',
      hoverColor: 'hover:border-pink-300',
      questions: '15 questions'
    },
    {
      id: 'compatibility-challenge',
      name: 'Compatibility Challenge',
      description: 'Test how compatible you are! Answer questions and compare your answers.',
      icon: '🧠',
      iconComponent: Brain,
      color: 'from-purple-500 to-indigo-500',
      hoverColor: 'hover:border-purple-300',
      questions: '25 questions'
    },
    {
      id: 'ping-pong',
      name: 'Ping Pong Game',
      description: 'Play a fun ping pong game! Share photos and compete for 100 points to win a prize!',
      icon: '🏓',
      iconComponent: Gamepad2,
      color: 'from-green-500 to-emerald-500',
      hoverColor: 'hover:border-green-300',
      questions: 'Score 100 to win!'
    }
  ];

  const handleInviteToPlay = (gameId) => {
    // US AGAIN has its own page
    if (gameId === 'us-again') {
      navigate('/us-again');
      return;
    }
    // Navigate to game invite page
    navigate(`/games/invite/${gameId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => navigate('/discover')}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 text-transparent bg-clip-text">
                🎮 Couples Games
              </h1>
              <p className="text-sm text-gray-600">Play fun games with your matches</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Instructions */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-4 border border-purple-200">
          <h3 className="font-bold text-purple-800 mb-2">How to Play:</h3>
          <ol className="text-sm text-purple-700 space-y-1">
            <li>1. Choose a game below</li>
            <li>2. Click &quot;Invite to Play&quot;</li>
            <li>3. Select a match from your connections</li>
            <li>4. Send them a game invite in chat!</li>
          </ol>
        </div>

        {/* Games Grid */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">🎯 Choose a Game</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {games.map((game) => {
              const IconComponent = game.iconComponent;
              return (
                <div
                  key={game.id}
                  className={`bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-transparent ${game.hoverColor} relative`}
                >
                  {/* NEW Badge */}
                  {game.isNew && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                      ✨ NEW
                    </div>
                  )}
                  
                  {/* Game Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${game.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <span className="text-3xl">{game.icon}</span>
                  </div>
                  
                  {/* Game Info */}
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{game.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{game.description}</p>
                  
                  {/* Game Details */}
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <IconComponent className="w-4 h-4 mr-1" />
                    <span>{game.questions}</span>
                  </div>

                  {/* Invite Button */}
                  <Button
                    onClick={() => handleInviteToPlay(game.id)}
                    className={`w-full bg-gradient-to-r ${game.color} hover:opacity-90 text-white font-semibold`}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Invite to Play
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-white/80 rounded-2xl p-6 shadow-lg">
          <h3 className="font-bold text-gray-800 mb-3">💡 Tips for Better Connections</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>• <strong>Get to Know You</strong> - Great for breaking the ice with new matches</li>
            <li>• <strong>Love & Psychology Quiz</strong> - Discover each other&apos;s love languages</li>
            <li>• <strong>Compatibility Challenge</strong> - See how well you match on important topics</li>
            <li>• <strong>Ping Pong</strong> - Have fun competing and share photos!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GamesPage;
